import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import Cookies from "js-cookie";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import advancedFormat from "dayjs/plugin/advancedFormat";
import SchedulerAdditionalFields from "../AppointmentSchedulerV2/SchedulerAdditionalFields";
import ExistingCustomer from "../AppointmentSchedulerV2/ExistingCustomer";
import SchedulerThankYouComponent from "../AppointmentSchedulerV2/SchedulerThankYouComponent";
import { scheduleAppointment } from "@/services";
import getLeadValues from '@/services/getLeadValues';
import CalendarComponent from "../Scheduler/CalendarComponentV2";
import { UniversalSchedulerStyles } from "./UniversalSchedulerStyles";
import {
  schedulerContextDefault,
  SchedulerContext,
} from "../context/Scheduler.context";
import validatePaidCampaign from "@helpers/validatePaidCampaign";
import localizationData from "../../data/lpLocalizationData";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

/**
 * Scheduler popup to be shown for setting appointments across site
 * @param {Object} account If user has an accout, it's set here to get show existing info
 * @param {Object} data WP data setting strings and images to be used across scheduler
 * @param {Function} callBack Callback function to be used to set the appointment
 * @param {Function} onSuccess Function to be called after appointment is successfull
 * @param {Boolean} update Use if appointment is being edited
 * @param {Object} leadValues Used for appointments lead, campaign and channel values stored
 * @param {Object} AdditionalQuestions Optional param, use if you want to override scheduler form questions
 * @param {String} customClass Custom class to be used when CSS has to be overriden on a particular page
 * @param {String} phoneNumber Phone number to be shown after appointment iss set
 * @param {String} language Used for localization. Only accepts 'en' or 'es'
 */
const UniversalSchedulerV2 = ({
  account = null,
  data,
  callBack,
  onSuccess,
  update = false,
  onClose,
  restartOnClose = false,
  PaidCampaign,
  phoneNumber = "18002226868",
  AdditionalQuestions,
  onDateSelect,
  customSubmitting,
  EntityCampaign = false,
  language = "en",
  setCurrentStep = false,
  setIsLoading = false,
  customClass = false,
  NumDaysOverride = null,
  AssignOwner = false,
  doElite = false,
  isTaxServicesLLC,
  EForm = false,
  hideCloseButton = false,

}) => {
  const { scheduler, form, thank_you } = data;
  const paid_campaign = validatePaidCampaign(PaidCampaign);

  const [error, setError] = useState(null);
  const [schedulerErrorMessage, setSchedulerErrorMessage] = useState(null);
  const [schedulerState, setSchedulerState] = useState(schedulerContextDefault);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [stage, setStage] = useState("scheduler");
  const [submitting, setSubmitting] = useState(false);
  const [userTimezone, setUserTimezone] = useState();
  const [localizationStrings, setLocalizationData] = useState(
    localizationData.en
  );
  const [realAccountantDetails, setRealAccountantDetails] = useState(null);

  useEffect(() => {
    const utz = dayjs.tz.guess();
    setUserTimezone(utz);
  }, []);

  useEffect(() => {
    if (language === "es") {
      setLocalizationData(localizationData.es);
    }
  }, [language]);

  // when selectedAppointment is updated through confirm on AppointmentScheduler component, it will submit the appointment if coming from TSF.
  useEffect(() => {
    if (setCurrentStep && selectedAppointment) {
      submit();
    }
  }, [selectedAppointment]);

  // Get the leadID and set the schedulerState to have the leadID for the scheduleAppointment function, if coming from TSF.
  useEffect(() => {
    if (setCurrentStep) {
      (async () => {
        const contactID = Cookies.get("ContactID");
        setSchedulerState({ leadID: contactID });
      })();
    }
  }, []);

  // Properly set the lead or contact id if the appointment is made for an existing account
  useEffect(() => {
    if (update) {
      const contactID = Cookies.get("ContactID");
      const leadID = Cookies.get("LeadID");
      setSchedulerState({ leadID: leadID, contactID: contactID });
    }
  }, []);

  const closeModals = () => {
    setSelectedAppointment(null);
    if (onClose) {
      onClose();
    }
    if (restartOnClose) {
      setStage("scheduler");
      return;
    }
    setStage(null);
  };

  const submit = async (formData, context, pauseExistingCustomer = true) => {
    setSubmitting(true);
    setError(null);
    setSchedulerErrorMessage('')
    // If the appointment comes throughn TSF(setCurrentStep as a prop), set a discovery appointment
    const discovery = setCurrentStep ? true : false;
    const leadValues = getLeadValues();
    const scheduleAppointmentResponse = await scheduleAppointment(
      selectedAppointment,
      paid_campaign,
      formData,
      phoneNumber,
      userTimezone,
      schedulerState,
      setSchedulerState,
      setError,
      update,
      EntityCampaign,
      language,
      AssignOwner,
      discovery,
      doElite,
      EForm,
      pauseExistingCustomer
    );

    // If the call to schedule appointment fails, return the user to the scheduler
    // 502 Gateway error returns undefined as the scheduleAppointmentResponse
    if (!scheduleAppointmentResponse || scheduleAppointmentResponse === undefined) {
      setSubmitting(false);
      setStage("scheduler");
      setSchedulerErrorMessage('Unfortunately, there was an issue setting your appointment, please try again or contact the phone number above.');
      return false;
    }

    const redirectExistingCustomer = scheduleAppointmentResponse?.redirectExistingCustomer
    if (redirectExistingCustomer) {
      setSubmitting(false);
      setStage("existing-customer");
      return false;
    }

    // Real accountant data
    if (
      scheduleAppointmentResponse &&
      scheduleAppointmentResponse.data &&
      scheduleAppointmentResponse.data.data
    ) {
      setRealAccountantDetails(scheduleAppointmentResponse.data.data);

      if (scheduleAppointmentResponse.data.data.Result == 'Error') {
        setSubmitting(false);
        setStage("scheduler");
        setSchedulerErrorMessage('Unfortunately, this appointment is no longer available. Please select a new time.')
        return false;
      }
    }

    if (onSuccess) {
      onSuccess();
    } else {
      if (callBack) {
        callBack({
          response: scheduleAppointmentResponse,
          formData,
          leadValues,
          selectedAppointment,
        });
      }
      setSubmitting(false);
      setStage("thanks");
    }
  };

  useEffect(() => {
    // if parent has other thank-you, bail out early and route through parent
    if (selectedAppointment && !setCurrentStep) {
      setStage("form");
    }
  }, [selectedAppointment]);

  const handleNextStep = (payload) => {
    // Use an async iife if the handleNextStep is coming from TSF, to properly set a selectedAppointment and use it's value in scheduleAppointment.
    if (setCurrentStep) {
      (async () => {
        setSelectedAppointment(payload);
      })();
    } else {
      setSelectedAppointment(payload);
    }
  };

  return (
    <SchedulerContext.Provider
      value={{ ...schedulerState, set: setSchedulerState }}
    >
      <UniversalSchedulerStyles data-cy="universal-scheduler">
        {stage === "scheduler" && (
          <CalendarComponent
            handleNextStep={onDateSelect || handleNextStep}
            stage={stage}
            data={scheduler}
            formData={form}
            close={closeModals}
            submitting={submitting || customSubmitting}
            PaidCampaign={paid_campaign}
            EntityCampaign={EntityCampaign}
            localizationStrings={localizationStrings}
            setCurrentStep={setCurrentStep}
            setIsLoading={setIsLoading}
            customClass={customClass}
            NumDaysOverride={NumDaysOverride}
            isTaxServicesLLC={isTaxServicesLLC}
            EForm={EForm}
            errorMessage={schedulerErrorMessage}
          />
        )}

        {stage === "form" && (
          <SchedulerAdditionalFields
            appointment={selectedAppointment}
            onBack={() => setStage("scheduler")}
            account={account}
            campaign={form.service_type}
            close={closeModals}
            stage={stage}
            submit={submit}
            submitError={error}
            data={scheduler}
            AdditionalQuestions={AdditionalQuestions}
            localizationFields={localizationStrings}
            title={localizationStrings.schedulerTitle}
            tosText={localizationStrings.tosText}
            buttonText={localizationStrings.schedulerButtonText}
            hideCloseButton={hideCloseButton}
          />
        )}

        {stage === "existing-customer" && (
          <ExistingCustomer
            onBack={() => setStage("scheduler")}
            close={closeModals}
            hideCloseButton={hideCloseButton}
            submit={submit}
            stage={stage}
          />
        )}

        {stage === "thanks" && (
          <SchedulerThankYouComponent
            appointment={selectedAppointment}
            title={thank_you.title}
            subTitle={thank_you.sub_title}
            accountant={scheduler.accountant_details}
            calendarEventTitle={thank_you.calendar_event_title}
            calendarEventDetails={thank_you.calendar_event_description}
            close={restartOnClose ? closeModals : null}
            localizationStrings={localizationStrings}
            realAccountantDetails={realAccountantDetails}
            customClass={customClass}
          />
        )}
      </UniversalSchedulerStyles>
    </SchedulerContext.Provider>
  );
};

export default UniversalSchedulerV2;
